<template>
    <div class="clients_card">
        <div class="card_header is-flex is-align-items-center">
            <PieChartIcon class="header_icon" />
            <span class="card_title">Clientes</span>
        </div>

        <div class="search_wrap">
            <svg class="search_icon" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"
                aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" />
            </svg>
            <input type="search"
                class="search_input"
                :value="value"
                placeholder="Buscar clientes"
                @input="$emit('input', $event.target.value)" />
        </div>

        <ul v-if="clients.length > 0" class="ranking_list">
            <li v-for="client in clients" :key="client.name"
                class="ranking_item"
                role="button"
                tabindex="0"
                @click="onClientClick(client)"
                @keydown.enter.prevent="onClientClick(client)"
                @keydown.space.prevent="onClientClick(client)">
                <span class="ranking_label"
                    v-tooltip.top="client.name.length > 20 ? client.name : ''">
                    {{ client.name }}
                </span>
                <div class="ranking_bar_track">
                    <div class="ranking_bar_fill"
                        :style="{ width: barWidth(client.share) }"></div>
                </div>
                <span class="ranking_meta">
                    <span class="ranking_amount">{{ formatMoney(client.amount) }}</span>
                    <span class="ranking_dot" aria-hidden="true">·</span>
                    <span class="ranking_share">{{ formatShare(client.share) }}</span>
                </span>
            </li>
        </ul>

        <p v-else class="empty_state">
            Sin resultados para "{{ value }}".
        </p>
    </div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";

export default {
    name: "ClientsRankingCard",
    components: { PieChartIcon },
    // v-model: value/input. El padre (BodySalesAnalysis) hace el bridge con
    // el store. La card queda presentational — los specs no necesitan Vuex.
    model: { prop: "value", event: "input" },
    props: {
        clients: {
            type: Array,
            required: true,
            validator: (arr) => arr.every((c) => typeof c.name === "string"),
        },
        value: { type: String, default: "" },
    },
    methods: {
        sharePercent(share) {
            const v = Math.max(0, Math.min(1, share ?? 0));
            return Math.round(v * 100);
        },
        barWidth(share) {
            return `${this.sharePercent(share)}%`;
        },
        formatMoney(v) {
            return parseToMoney(v);
        },
        formatShare(share) {
            return `${this.sharePercent(share)}%`;
        },
        // Drill-down contract: el caller decide qué hacer con el click. Por
        // ahora no hay listener (otra iteración abrirá un modal por cliente);
        // el evento existe para que la API del componente esté lista.
        onClientClick(client) {
            this.$emit("client-click", client);
        },
    },
};
</script>

<style scoped lang="scss">
.clients_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: 0 1px 2px rgba(10, 13, 20, 0.03);
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.card_header {
    margin-bottom: 12px;
    color: var(--report-text);
    flex-shrink: 0;

    .header_icon {
        margin-right: 8px;
        flex-shrink: 0;
    }

    .card_title {
        font-size: 14px;
        font-weight: 600;
    }
}

.search_wrap {
    position: relative;
    margin-bottom: 12px;
    flex-shrink: 0;

    .search_icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        width: 16px;
        height: 16px;
        color: var(--report-text-muted-soft);
        pointer-events: none;
    }
}

.search_input {
    width: 100%;
    // padding-left genera el espacio para el ícono absoluto a la izquierda.
    padding: 10px 12px 10px 38px;
    border: 1px solid var(--report-border);
    border-radius: 999px;
    background: var(--report-bg);
    font-size: 14px;
    color: var(--report-text);
    box-sizing: border-box;

    &::placeholder {
        color: var(--report-text-muted-soft);
    }

    &:focus {
        outline: none;
        border-color: var(--report-accent-text);
        box-shadow: 0 0 0 3px var(--report-accent-bg);
    }
}

.ranking_list {
    list-style: none;
    margin: 0;
    padding: 0;
    // El clamp lo dicta la card (overflow:hidden + flex column). Antes
    // teníamos max-height: 460px arbitrario que pesaba en cards más cortas
    // y no escalaba a viewports altos.
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.ranking_item {
    display: grid;
    // 3 columnas (mismo pattern que Stores): label / bar / meta. Sin
    // separadores visuales entre filas — el padding mantiene el ritmo.
    grid-template-columns: minmax(80px, 140px) 1fr auto;
    gap: 16px;
    align-items: center;
    padding: 6px 8px;
    font-size: 13px;
    border-radius: 8px;

    // Drill-down affordance: cursor + hover + focus visible (accesibilidad
    // por teclado vía tabindex+@keydown). La transición es corta — coherente
    // con el "micro-interaction whisper" del sistema.
    cursor: pointer;
    transition: background-color 120ms ease;

    &:hover {
        background: var(--report-surface);
    }

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 2px;
        background: var(--report-surface);
    }
}

.ranking_label {
    color: var(--report-text);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ranking_bar_track {
    background: var(--report-progress-bg);
    border-radius: 999px;
    height: 13px;
    overflow: hidden;
}

.ranking_bar_fill {
    height: 100%;
    border-radius: 999px;
    // Single accent color — sin tiers (los rankings largos no admiten
    // codificación cromática por posición). El token vive separado de los
    // chart-segment-* porque su semántica es distinta (uniform vs gradient).
    background: var(--report-chart-accent);
    transition: width 200ms ease-out;
}

.ranking_meta {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    color: var(--report-text-muted);
    font-size: 13px;
    white-space: nowrap;

    .ranking_amount {
        color: var(--report-text-muted-strong);
        font-weight: 500;
    }

    .ranking_dot {
        color: var(--report-text-muted-soft);
        font-weight: 700;
    }

    .ranking_share {
        color: var(--report-text-muted);
        font-weight: 500;
    }
}

.empty_state {
    color: var(--report-text-muted);
    font-size: 13px;
    text-align: center;
    padding: 24px 8px;
}
</style>
